'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { WIDGET_FOOTER_BTN, IconSuggest, IconDownload, IconEmbed, useIsPopup } from './widgetFooterUi'

// The shared footer every widget shows under the tool: three buttons (suggest a
// widget, install as an app, get the free embeddable widget) plus the
// Calculator Scope logo. In popup/embed mode (?do=pop) the buttons are hidden
// and only the logo is shown.

type Props = {
    lang: string
    toolSlug?: string
    toolId?: string
    h1?: string
    h1En?: string
    widgetPageSlug?: string
    translations: {
        suggest: string
        downloadWidget: string
        getWidget: string
    }
}

export default function WidgetFooter({ lang, toolSlug, toolId, h1, h1En, widgetPageSlug, translations }: Props) {
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null)
    const isPopup = useIsPopup()

    // PWA install prompt handling (beforeinstallprompt).
    useEffect(() => {
        if (typeof window === 'undefined') return
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault()
            setDeferredPrompt(e)
        }
        const nav = (window as any).navigator
        const standalone = (nav && nav.standalone) || window.matchMedia('(display-mode: standalone)').matches
        if (!standalone) {
            window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
        }
        return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }, [])

    const pushGA = (payload: Record<string, unknown>) => {
        if (typeof window !== 'undefined' && (window as any).dataLayer) {
            (window as any).dataLayer.push({
                eventCategory: 'Widget',
                widgetToolId: toolId || 'unknown',
                widgetH1: h1 || 'Unknown',
                widgetH1En: h1En || h1 || 'Unknown',
                widgetLang: lang,
                ...payload,
            })
        }
    }

    const popupUrl = toolSlug ? `/${lang}/${toolSlug}?do=pop` : `/${lang}?do=pop`

    const handleInstall = async () => {
        if (!deferredPrompt) {
            if (typeof window === 'undefined') return
            if (!window.location.search.includes('do=pop')) {
                pushGA({ event: 'pwa_install_attempt', eventAction: 'PWA Install Attempt', eventLabel: 'Redirecting to Popup' })
                window.open(popupUrl, '_blank', 'width=400,height=600')
            }
            return
        }
        try {
            deferredPrompt.prompt()
            const { outcome } = await deferredPrompt.userChoice
            pushGA({ event: 'pwa_install_prompt', eventAction: 'PWA Install Prompt', eventLabel: outcome === 'accepted' ? 'Installed' : 'Dismissed' })
            setDeferredPrompt(null)
        } catch {
            if (typeof window !== 'undefined') window.open(popupUrl, '_blank', 'width=400,height=600')
        }
    }

    const getWidgetHref = toolSlug
        ? `/${lang}/widget/${toolSlug}`
        : widgetPageSlug
            ? `/${lang}/${widgetPageSlug}`
            : `/${lang}/widget`

    return (
        <>
            {!isPopup && (
                <div className="mt-5 flex flex-col items-start gap-2">
                    <Link href={`/${lang}/contact`} className={WIDGET_FOOTER_BTN}>
                        <IconSuggest />
                        <span>{translations.suggest}</span>
                    </Link>
                    <button onClick={handleInstall} type="button" className={`${WIDGET_FOOTER_BTN} cursor-pointer`}>
                        <IconDownload />
                        <span>{translations.downloadWidget}</span>
                    </button>
                    <Link href={getWidgetHref} className={WIDGET_FOOTER_BTN}>
                        <IconEmbed />
                        <span>{translations.getWidget}</span>
                    </Link>
                </div>
            )}

            <div className="mt-5">
                <Link
                    href={`/${lang}`}
                    className="logo-widget inline-block"
                    onClick={() => pushGA({ event: 'logo_click', eventAction: 'Logo Click', eventLabel: 'Logo Click', widgetReferrer: typeof document !== 'undefined' ? (document.referrer || window.location.href) : '' })}
                >
                    <Image
                        src="/calculatorscope-logo.svg"
                        alt="Calculator Scope"
                        width={90}
                        height={90}
                        className="object-contain inline-block"
                    />
                </Link>
            </div>
        </>
    )
}
